/* ============================================================
   c-price-slider.js
   価格ファセットのダブルつまみレンジスライダー（vertical サイドバー専用）。
   マークアップは snippets/c-price-slider.liquid（サーバーレンダリング）。

   仕組み:
   - Dawn の GTE / LTE テキスト入力は DOM に残す。JS がスライダーを
     「準備完了(c-price-slider--ready)」にすると CSS 側でテキスト入力を隠し、
     スライダーを表示する（JS 無効時は Dawn 標準入力のまま = フォールバック）。
   - つまみのドラッグ中（input イベント）は値表示とトラックの塗りだけ更新し、
     確定（change イベント = つまみを離した時 / キー操作の確定）で GTE / LTE
     入力へ書き戻し、input + change を dispatch して Dawn の FacetFiltersForm に
     送信させる。
   - range 入力自身の input / change は document のキャプチャ段階で
     stopPropagation する。そうしないと form の 'input' リスナー
     （FacetFiltersForm の debouncedOnSubmit）がドラッグ中に発火してしまう。
     キャプチャ段階で止めるため、委譲のままドラッグ中送信を防げる。

   assets/facets.js は変更しない。フィルタ操作のたびに Dawn が details の
   innerHTML を差し替えるため、.facets-container を MutationObserver で監視して
   再初期化する（c-facet-mode.js と同じパターン）。
   イベントは document への委譲なので再バインドは不要で、再初期化がやるのは
   「新しく差し込まれたスライダーの表示更新と ready 付与」だけ。
   ============================================================ */
(function () {
  'use strict';

  var SLIDER_SELECTOR = '[data-c-price-slider]';
  var RANGE_SELECTOR = '[data-c-price-range]';
  var READY_CLASS = 'c-price-slider--ready';
  var MIN_FRONT_CLASS = 'c-price-slider--min-front';

  var applying = false;
  var observers = [];

  function toNumber(value) {
    var n = parseFloat(value);
    return isNaN(n) ? 0 : n;
  }

  function getRangeInputs(slider) {
    return {
      min: slider.querySelector('[data-c-price-range="min"]'),
      max: slider.querySelector('[data-c-price-range="max"]'),
    };
  }

  /* 金額フォーマッタ。記号・小数桁は cart.currency 由来の data 属性から作る
     （Liquid の money フィルタと同じ通貨で揃える。直書きはしない）。 */
  function createFormatter(slider) {
    var code = slider.getAttribute('data-c-currency-code');
    var symbol = slider.getAttribute('data-c-currency-symbol') || '';
    var locale = document.documentElement.lang || undefined;
    var nf = null;

    if (code && window.Intl && typeof Intl.NumberFormat === 'function') {
      try {
        nf = new Intl.NumberFormat(locale, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' });
      } catch (e) {
        try {
          nf = new Intl.NumberFormat(locale, { style: 'currency', currency: code });
        } catch (e2) {
          nf = null;
        }
      }
    }

    return function (value) {
      if (nf) return nf.format(value);
      return symbol + value.toLocaleString(locale);
    };
  }

  function getFormatter(slider) {
    if (!slider.cPriceFormat) slider.cPriceFormat = createFormatter(slider);
    return slider.cPriceFormat;
  }

  /* レンジ幅に対して step:100 が粗すぎる（＝つまみの止まる位置が少なすぎる）場合だけ
     刻みを細かくする。基準値は Liquid 側の step 属性。 */
  function adjustStep(slider) {
    var io = getRangeInputs(slider);
    if (!io.min || !io.max) return;
    var rangeMax = toNumber(slider.getAttribute('data-c-price-max'));
    var step = toNumber(io.min.getAttribute('step')) || 1;
    if (rangeMax > 0 && rangeMax / step < 10) {
      step = Math.max(1, Math.round(rangeMax / 50));
      io.min.setAttribute('step', step);
      io.max.setAttribute('step', step);
    }
  }

  /* 表示（塗り・現在値・つまみの重なり順）を現在の input 値に合わせる。
     source は動かされた側（'min' / 'max' / null）。 */
  function refresh(slider, source) {
    var io = getRangeInputs(slider);
    if (!io.min || !io.max) return;

    var rangeMax = toNumber(slider.getAttribute('data-c-price-max')) || toNumber(io.max.getAttribute('max'));
    var step = toNumber(io.min.getAttribute('step')) || 1;
    var from = toNumber(io.min.value);
    var to = toNumber(io.max.value);

    // 下限が上限を越えないようにクランプ（動かした側を相手に合わせる）
    if (from > to) {
      if (source === 'max') {
        to = from;
        io.max.value = to;
      } else {
        from = to;
        io.min.value = from;
      }
    }

    var fill = slider.querySelector('[data-c-price-fill]');
    if (fill && rangeMax > 0) {
      var left = (from / rangeMax) * 100;
      var right = (to / rangeMax) * 100;
      fill.style.left = left + '%';
      fill.style.width = Math.max(0, right - left) + '%';
    }

    var format = getFormatter(slider);
    var minValueEl = slider.querySelector('[data-c-price-value="min"]');
    var maxValueEl = slider.querySelector('[data-c-price-value="max"]');
    var minText = format(from);
    var maxText = format(to);
    if (minValueEl && minValueEl.textContent !== minText) minValueEl.textContent = minText;
    if (maxValueEl && maxValueEl.textContent !== maxText) maxValueEl.textContent = maxText;

    // 両つまみが右端で重なると下限がつかめなくなるため、その時だけ下限を前面に出す
    slider.classList.toggle(MIN_FRONT_CLASS, rangeMax > 0 && from >= rangeMax - step);
  }

  function findPriceField(slider, suffix, index) {
    var scope = slider.parentElement;
    if (!scope) return null;
    var priceRange = scope.querySelector('price-range');
    if (!priceRange) return null;
    return priceRange.querySelector('input[name$=".' + suffix + '"]') || priceRange.querySelectorAll('input')[index];
  }

  /* Dawn の GTE / LTE テキスト入力へ書き戻す。値が変わったときだけ
     change（PriceRange の正規化用）→ input（FacetFiltersForm の送信用）を投げる。 */
  function setPriceField(input, value) {
    if (!input || input.value === value) return false;
    input.value = value;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('input', { bubbles: true }));
    return true;
  }

  function commit(slider) {
    var io = getRangeInputs(slider);
    if (!io.min || !io.max) return;

    var rangeMax = toNumber(slider.getAttribute('data-c-price-max')) || toNumber(io.max.getAttribute('max'));
    var from = toNumber(io.min.value);
    var to = toNumber(io.max.value);

    // 端いっぱいなら空にして絞り込み条件そのものを外す
    setPriceField(findPriceField(slider, 'gte', 0), from > 0 ? String(from) : '');
    setPriceField(findPriceField(slider, 'lte', 1), to < rangeMax ? String(to) : '');
  }

  function closestSlider(element) {
    return element && element.closest ? element.closest(SLIDER_SELECTOR) : null;
  }

  function isRangeInput(element) {
    return !!(element && element.matches && element.matches(RANGE_SELECTOR));
  }

  /* キャプチャ段階で止める（form の 'input' リスナーへ到達させない） */
  function onInputCapture(event) {
    if (!isRangeInput(event.target)) return;
    event.stopPropagation();
    var slider = closestSlider(event.target);
    if (slider) refresh(slider, event.target.getAttribute('data-c-price-range'));
  }

  function onChangeCapture(event) {
    if (!isRangeInput(event.target)) return;
    event.stopPropagation();
    var slider = closestSlider(event.target);
    if (!slider) return;
    refresh(slider, event.target.getAttribute('data-c-price-range'));
    commit(slider);
  }

  function init() {
    if (applying) return;
    applying = true;
    try {
      document.querySelectorAll(SLIDER_SELECTOR).forEach(function (slider) {
        if (slider.classList.contains(READY_CLASS)) return;
        adjustStep(slider);
        refresh(slider, null);
        slider.classList.add(READY_CLASS);
      });
    } finally {
      applying = false;
    }
  }

  function observe() {
    observers.forEach(function (o) {
      o.disconnect();
    });
    observers = [];
    document.querySelectorAll('.facets-container').forEach(function (root) {
      var mo = new MutationObserver(function () {
        if (applying) return;
        init();
      });
      mo.observe(root, { childList: true, subtree: true });
      observers.push(mo);
    });
  }

  function start() {
    init();
    observe();
  }

  document.addEventListener('input', onInputCapture, true);
  document.addEventListener('change', onChangeCapture, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', start);
  }
})();
